// src/webideal-recruit/pages/Candidates.jsx

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { backendApi } from "@/api/backendApi";
import { motion } from "framer-motion";

import { createPageUrl } from "@/utils";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

import {
  Search,
  MapPin,
  Clock,
  Star,
  MoreVertical,
  ChevronDown,
} from "lucide-react";

// Candidate Card Component
function CandidateCard({ candidate }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-slate-100 p-6 mb-6 hover:shadow-lg transition-all"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-violet-100 flex items-center justify-center text-violet-700 font-semibold text-lg">
            {candidate.full_name
              .split(" ")
              .map((n) => n[0])
              .join("")}
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {candidate.full_name}
            </h3>
            <p className="text-sm text-slate-500">
              {candidate.title || "Candidate"}
              {candidate.company ? ` at ${candidate.company}` : ""}
            </p>

            <div className="flex gap-4 mt-2 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" /> {candidate.location}
              </span>

              {candidate.experience_years && (
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {candidate.experience_years} years
                </span>
              )}

              {candidate.rating && (
                <span className="flex items-center gap-1 text-amber-500">
                  <Star className="w-4 h-4" />
                  {candidate.rating}/5
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              {candidate.tags?.map((tag, i) => (
                <Badge
                  key={i}
                  className="bg-slate-100 text-slate-600 rounded-md"
                >
                  {tag}
                </Badge>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              {candidate.skills?.slice(0, 6).map((skill, i) => (
                <Badge
                  key={i}
                  className="bg-indigo-50 text-indigo-700 rounded-md"
                >
                  {skill}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <MoreVertical className="w-5 h-5 text-slate-400 cursor-pointer" />
      </div>
    </motion.div>
  );
}

export default function Candidates() {
  const [search, setSearch] = useState("");

  const { data: candidatesData = [], isLoading } = useQuery({
    queryKey: ["candidates"],
    queryFn: () => backendApi.entities.Candidate.list("-created_date"),
  });

  const candidates = Array.isArray(candidatesData) ? candidatesData : [];

  const filtered = candidates.filter((c) =>
    c.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Candidates</h1>
            <p className="text-slate-500 mt-1">
              Manage and search your candidate database
            </p>
          </div>

          <Link to={createPageUrl("CreateCandidate")}>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2">
              + Add Candidate
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Search + Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-slate-200 shadow-sm p-4 rounded-2xl flex flex-wrap gap-4 items-center"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search by name, email, title, or skills..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-slate-200 rounded-xl"
          />
        </div>

        <Button variant="outline" className="rounded-xl border-slate-300">
          All Status <ChevronDown className="w-4 h-4 ml-2" />
        </Button>

        <Button variant="outline" className="rounded-xl border-slate-300">
          All Sources <ChevronDown className="w-4 h-4 ml-2" />
        </Button>

        <Button variant="outline" className="rounded-xl border-slate-300">
          Newest First <ChevronDown className="w-4 h-4 ml-2" />
        </Button>

        <Button variant="outline" className="rounded-xl border-slate-300">
          <Search className="w-4 h-4 mr-2" /> More Filters
        </Button>
      </motion.div>

      {/* Count */}
      <div className="text-sm text-slate-500">
        Showing {filtered.length} of {candidates.length} candidates
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40 rounded-2xl" />
          ))}
        </div>
      )}

      {/* Candidate List */}
      {!isLoading &&
        filtered.map((candidate) => (
          <CandidateCard key={candidate.id} candidate={candidate} />
        ))}
    </div>
  );
}
